import Icon, { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import { Alert, Button, Form, Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import projectMemberApi from '../../../../../api/ProjectMemberApi';
import { ProjectMemberImport, ProjectMemberResult, Role, TaskMember } from '../../../../../model/entities';
import { ReactComponent as SaveSvg } from '../../../../../resources/images/save.svg';
import notificationService from '../../../../../services/NotificationService';
import styles from './SummaryComponent.module.scss';

/**
 * Returns the project members import page summary.
 * @props the props
 * @returns the project members import page summary.
 */
const SummaryComponent = (props: Props): React.ReactElement => {
    const { onBack, onSaveMembers, projectMemberImport } = props;
    /*** HOOKS ***/

    const [form] = Form.useForm();
    const intl = useIntl();
    const [saving, setSaving] = useState<boolean>();

    /*** EFFECTS ***/

    /*** METHODS ***/

    const upload = async () => {
        try {
            setSaving(true);

            const result = await projectMemberApi.import(projectMemberImport);

            onSaveMembers(result);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setSaving(false);
        }
    };

    /*** COMPONENTS ***/

    const projectMemberResults = projectMemberImport.results;
    const disabled = projectMemberImport.results.some((r) => !!r.errors && r.errors.length > 0);
    const columns: ColumnsType<ProjectMemberResult> = [
        {
            title: <FormattedMessage id="projectMember.status" />,
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: 100,
            render: (value: string, projectMemberResult: ProjectMemberResult) => (
                <>
                    {projectMemberResult.errors && projectMemberResult.errors.length > 0 ? (
                        <Tooltip title={<FormattedMessage id="projectMembers.import.steps.2.errors.company" />}>
                            <ExclamationCircleFilled className="failure" />
                        </Tooltip>
                    ) : (
                        <CheckCircleFilled className="success" />
                    )}
                </>
            ),
        },
        {
            title: <FormattedMessage id="projectMember.email" />,
            dataIndex: 'email',
            key: 'email',
            width: 350,
            render: (value: string, projectMemberResult: ProjectMemberResult) =>
                projectMemberResult.projectMember.email,
        },
        {
            title: <FormattedMessage id="projectMember.company" />,
            dataIndex: 'company',
            key: 'company',
            width: 200,
            render: (value: string, projectMemberResult: ProjectMemberResult) =>
                projectMemberResult.projectMember.company?.name,
        },
        {
            title: <FormattedMessage id="projectMember.projectAdmin" />,
            dataIndex: 'projectAdmin',
            key: 'projectAdmin',
            width: 250,
            align: 'center',
            render: (value: boolean, projectMemberResult: ProjectMemberResult) =>
                projectMemberResult.projectMember.projectAdmin ? (
                    <FormattedMessage id="button.yes" />
                ) : (
                    <FormattedMessage id="button.no" />
                ),
        },
        {
            title: <FormattedMessage id="projectMember.roles" />,
            dataIndex: 'roles',
            key: 'roles',
            render: (value: Role[], projectMemberResult: ProjectMemberResult) =>
                projectMemberResult.projectMember.roles.map((r) => r.name).join(', '),
        },
        {
            title: <FormattedMessage id="projectMember.products" />,
            dataIndex: 'products',
            key: 'products',
            render: (value: any, projectMemberResult: ProjectMemberResult) =>
                projectMemberResult.projectMember.services
                    .filter((s) => s.access === 'member' || s.access === 'administrator')
                    .map((s) => intl.formatMessage({ id: `projectMember.products.${s.name}` }))
                    .join(', '),
        },
    ];

    return (
        <>
            <Alert
                message={<FormattedMessage id="projectMembers.import.steps.2.warning.title" />}
                description={<FormattedMessage id="projectMembers.import.steps.2.warning.description" />}
                type="info"
                showIcon
            />
            <Form form={form} component={false}>
                <h3 className={styles.subtitle}>
                    <FormattedMessage id="projectMembers.import.steps.2.members" />
                </h3>
                <Table
                    dataSource={projectMemberResults}
                    columns={columns}
                    rowKey="email"
                    pagination={false}
                    showSorterTooltip={false}
                />
            </Form>
            <Space className={styles.buttons}>
                <Button
                    type="primary"
                    size="large"
                    onClick={upload}
                    icon={<Icon component={SaveSvg} />}
                    loading={saving}
                    disabled={disabled}
                >
                    <FormattedMessage id="button.save" tagName="span" />
                </Button>
                <Button type="text" size="large" onClick={onBack}>
                    <FormattedMessage id="button.back" tagName="span" />
                </Button>
            </Space>
        </>
    );
};

export default SummaryComponent;

interface Props {
    projectMemberImport: ProjectMemberImport;
    onSaveMembers: (result: TaskMember) => void;
    onBack: () => void;
}
